// ScrollUpDownFab — global "jump to top / jump to bottom" floating
// button. Sits on the LEFT edge (so it never collides with the
// right-side stack of voice-mic, AskAI, and BackgroundMusicPlayer),
// vertical-center, and is mounted from Layout.jsx so EVERY page
// has it without the page itself opting in.
//
// Why a single double-arrow instead of two separate buttons?
//   • The user said: "the up-and-down arrow … I still can't get to
//     it at the bottom every screen". A single chip with two
//     stacked carets gives them BOTH affordances in a 40×56 px
//     target — small enough to never block content, big enough for
//     a thumb.
//   • Active behavior switches based on scroll position. If you're
//     within 200 px of the top, the chip only shows the DOWN arrow
//     (jump to bottom). If you're within 400 px of the bottom, it
//     only shows the UP arrow (jump to top). In-between, both are
//     visible so the user can dart either direction.
//
// Mobile-safe positioning:
//   • Anchored to the LEFT edge (left:0) so it can't be hidden by
//     phone navigation gestures or the right-side floating stack.
//   • Vertical center (top:50%) so it's reachable whether the
//     keyboard's open or not — never collides with BottomNav.
//   • Hides automatically when the page is too short to scroll
//     (≤ 1.5 viewports of content) — no clutter on tiny pages.
import React, { useEffect, useState } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

export default function ScrollUpDownFab() {
  const [scrollY, setScrollY] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setScrollY(window.scrollY || window.pageYOffset || 0);
        setMaxScroll(Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Skip rendering entirely on pages that don't scroll meaningfully.
  // Threshold = half a viewport so chat / settings / single-card pages
  // stay clean.
  if (maxScroll < 600) return null;

  const nearTop = scrollY < 200;
  const nearBottom = scrollY > maxScroll - 400;

  const jumpUp = () => {
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { window.scrollTo(0, 0); }
  };
  const jumpDown = () => {
    try {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
    } catch (_) {
      window.scrollTo(0, document.documentElement.scrollHeight);
    }
  };

  return (
    <div
      data-testid="scroll-up-down-fab"
      className="fixed left-2 z-40 select-none flex flex-col gap-1"
      style={{ top: "50%", transform: "translateY(-50%)" }}
    >
      {!nearTop && (
        <button
          type="button"
          onClick={jumpUp}
          data-testid="scroll-up-fab"
          aria-label={t("action.scrollTop")}
          title={t("action.scrollTop")}
          className="w-5 h-5 rounded-full bg-forest/70 text-cream shadow-sm grid place-items-center hover:bg-forest active:scale-95 transition border border-cream/10 backdrop-blur"
        >
          <CaretUp size={9} weight="bold" />
        </button>
      )}
      {!nearBottom && (
        <button
          type="button"
          onClick={jumpDown}
          data-testid="scroll-down-fab"
          aria-label={t("action.scrollBottom")}
          title={t("action.scrollBottom")}
          className="w-5 h-5 rounded-full bg-forest/70 text-cream shadow-sm grid place-items-center hover:bg-forest active:scale-95 transition border border-cream/10 backdrop-blur"
        >
          <CaretDown size={9} weight="bold" />
        </button>
      )}
    </div>
  );
}
